import { API_URL } from '../constants';
import { type Stripe as StripeType, type StripeCardNumberElement } from '@stripe/stripe-js';

export const sendStripeReq = async (
  stripe: StripeType,
  card: StripeCardNumberElement,
  name: string,
): Promise<any> => {
  try {
    return await stripe.createPaymentMethod({
      type: 'card',
      card,
      billing_details: {
        name,
      },
    });
  } catch (e) {
    return e;
  }
};

interface ResponseDataProps {
  payment_intent_secret?: string;
  payment_intent_id?: string;
  checkout_session_id?: string;
  checkout_session_secret?: string;
  subscription_id?: string;
  event_id?: string;
  price_with_discount?: number;
  customer_id?: string | number;
}

export type PollingErrors = {
  messages: string;
  decline_code?: string;
  processor_response?: string;
};

export const subscribeToPriceChange = async (
  pollingId: string,
): Promise<{
  status?: string;
  errors?: PollingErrors;
  data?: ResponseDataProps & { decline_code?: string; processor_response?: string };
}> => {
  const url = `${API_URL}/polling/get-result/${pollingId}`;

  try {
    const responseData = await fetch(url, { method: 'GET' });
    const response = await responseData.json();
    const { data, errors } = response;

    if (responseData.status === 202) {
      return await subscribeToPriceChange(pollingId);
    }

    if (data) {
      return {
        ...data.result_data,
        data: {
          ...data.result_data.data,
          subscription_id: data.result_data.subscription_id,
          event_id: data.result_data.event_id,
          price_with_discount: data.result_data.price_with_discount,
          customer_id: data.result_data.customer_id,
        },
      };
    } else {
      return { errors };
    }
  } catch (e) {
    console.error(e);
    return { errors: { messages: 'An error has occurred (look at console)' } };
  }
};

export type StripePollingOptions = {
  alternatePaymentMethod?:
    'revolut_pay' | 'pix_automatico' | 'upi' | 'blik' | 'amazon_pay' | 'cashapp';
  email?: string;
  checkoutSession?: boolean;
  returnUrl?: string;
  checkoutSessionId?: string;
};

const returnsFullPollingPayload = (options?: StripePollingOptions): boolean =>
  Boolean(options?.alternatePaymentMethod || options?.checkoutSession);

export const getPollingID = async (
  nonce: string,
  plan: string,
  token: string,
  analyticsParams: {
    user_data: object;
    [key: string]: string | object;
  },
  eventsData: object,
  onErrorHandler: (message: string, errorData?: Record<string, unknown>) => void,
  payment_intent_id: string = '',
  coupon: string = '',
  options?: StripePollingOptions,
) => {
  const url = `${API_URL}/users/me/subscriptions/polling/${plan}`;
  const requestData = {
    ...analyticsParams,
    user_data: {
      ...analyticsParams['user_data'],
      ...eventsData,
    },
  };
  const body: Record<string, unknown> = {
    gateway: 'stripe',
    coupon,
    payment_intent_id,
    ...requestData,
  };

  if (nonce) {
    body.payment_method_nonce = nonce;
  }

  if (options?.alternatePaymentMethod) {
    body.payment_method = options.alternatePaymentMethod;
    if (options.alternatePaymentMethod === 'pix_automatico' && options.email) {
      body.user_data = {
        ...(body.user_data as object),
        email: String(options.email).trim(),
      };
    }
    if (options.alternatePaymentMethod === 'blik' && options.email) {
      body.user_data = {
        ...(body.user_data as object),
        email: String(options.email).trim(),
      };
    }
    if (
      (options.alternatePaymentMethod === 'upi' || options.alternatePaymentMethod === 'cashapp') &&
      options.email
    ) {
      body.email = String(options.email).trim();
    }
  }

  if (options?.checkoutSession) {
    body.checkout_session = 'true';
    if (options.returnUrl) {
      body.return_url = options.returnUrl;
    }
    if (options.checkoutSessionId) {
      body.checkout_session_id = options.checkoutSessionId;
    }
  }

  try {
    const responseData = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(body),
    });
    const response = await responseData.json();
    const { data, errors } = response;

    if (data) {
      if (returnsFullPollingPayload(options)) {
        return data;
      }
      return data.polling_result_id;
    } else if (errors) {
      onErrorHandler(errors.messages, errors);
      return;
    }
  } catch (e) {
    console.error(e);
    onErrorHandler('An error has occurred (look at console)');
  }
};

export const getSubscriptionID = async (
  nonce: string,
  plan: string,
  token: string,
  analyticsParams: {
    user_data: object;
    [key: string]: string | object;
  },
  eventsData: object,
  coupon: string = '',
) => {
  const url = `${API_URL}/users/me/subscriptions/${plan}`;
  const requestData = {
    ...analyticsParams,
    user_data: {
      ...analyticsParams['user_data'],
      ...eventsData,
    },
  };

  const body = {
    gateway: 'braintree',
    payment_method_nonce: nonce,
    coupon,
    ...requestData,
  };

  try {
    const results = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(body),
    });
    return await results.json();
  } catch (e) {
    console.error(e);
    return { errors: { messages: 'An error has occurred (look at console)' } };
  }
};
